import { Maatregel, VergelijkingStatus } from "@/entities/vsm";
import { useMaatregelContext } from "@/features/vsm/maatregel-context";
import { useIsMaatregelInDagplan } from "@/features/vsm/use-dagplan-check";
import { useMaatregelVergelijking } from "@/features/vsm/vsm-vergelijking-context";
import { Badge, BadgeVariant, Button, Icon, Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components";
import { cn } from "@/shared/utils";

type MaatregelTagsProps = {
    maatregel: Maatregel;
    isBewerkbaar: boolean;
    className?: string;
    showVergelijkingTag?: boolean;
};

export function MaatregelTags({ maatregel, isBewerkbaar, className, showVergelijkingTag = true }: Readonly<MaatregelTagsProps>) {
    const { handmatigToegevoegd = false } = maatregel;
    const { status: vergelijkingStatus } = useMaatregelVergelijking(maatregel);
    const isVervallen = vergelijkingStatus === "Vervallen";

    return (
        <div className={cn("flex items-center gap-2 empty:hidden", className)}>
            {handmatigToegevoegd && <HandmatigTag variant="toegevoegd" />}
            {!handmatigToegevoegd && maatregel.status === "Bewerkt" && <HandmatigTag variant="bewerkt" />}
            {isBewerkbaar && <DagplanWaarschuwing maatregel={maatregel} />}
            {showVergelijkingTag && <VergelijkingTag status={vergelijkingStatus} />}
            {(handmatigToegevoegd || isVervallen) && <CopyNaarNieuwButton maatregelen={[maatregel]} title="Treinserie kopiëren" />}
        </div>
    );
}

const vergelijkingBadgeVariant: Record<Exclude<VergelijkingStatus, "Ongewijzigd">, BadgeVariant> = {
    Nieuw: "green-subtle",
    Gewijzigd: "yellow-subtle",
    Vervallen: "red-subtle",
};

export function VergelijkingTag({ status }: Readonly<{ status: VergelijkingStatus }>) {
    if (status === "Ongewijzigd") {
        return null;
    }

    return (
        <Badge variant={vergelijkingBadgeVariant[status]} data-testid={`vergelijking-tag-${status.toLowerCase()}`}>
            {status}
        </Badge>
    );
}

export function CopyNaarNieuwButton({ maatregelen, title }: Readonly<{ maatregelen: Maatregel[]; title?: string }>) {
    const { copyToNieuweVersie, isCopiedToNieuweVersie } = useMaatregelContext();
    const isCopied = maatregelen.every((maatregel) => isCopiedToNieuweVersie(maatregel));

    return (
        <Button
            iconOnly
            iconSize="xs"
            variant="tertiary"
            size="s"
            onClick={() => maatregelen.forEach((maatregel) => copyToNieuweVersie(maatregel))}
            disabled={isCopied}
            title={title}
            className="hidden group-data-[variant=oud]:inline-flex"
            icon="copy-to"
            data-testid="copy-maatregel-button"
        />
    );
}

type HandmatigTagProps = {
    variant: "toegevoegd" | "bewerkt";
};

function HandmatigTag({ variant }: Readonly<HandmatigTagProps>) {
    const isToegevoegd = variant === "toegevoegd";

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Badge className="px-1.5 py-1" variant="gray-subtle" data-testid={`handmatig-tag-${variant}`}>
                    <Icon name={isToegevoegd ? "plus-circle" : "edit"} size="xs" />
                </Badge>
            </TooltipTrigger>
            <TooltipContent>{isToegevoegd ? "Handmatig toegevoegd" : "Handmatig bewerkt"}</TooltipContent>
        </Tooltip>
    );
}

function DagplanWaarschuwing({ maatregel }: Readonly<{ maatregel: Maatregel }>) {
    const isInDagplan = useIsMaatregelInDagplan();

    if (isInDagplan(maatregel) !== false) {
        return null;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Icon name="alert-filled" className="text-content-emphasis" data-testid="dagplan-waarschuwing" />
            </TooltipTrigger>
            <TooltipContent className="max-w-none whitespace-nowrap">Serie niet gevonden in het dagplan</TooltipContent>
        </Tooltip>
    );
}
